import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';

import { getLineItemDisplay } from '../../helpers/lineItemDisplay';
import { LineItemEntry } from '../../types';
import { DragHandleIcon } from '../DragHandleIcon/DragHandleIcon';
import { LineItemIdentifiers } from '../LineItemIdentifiers/LineItemIdentifiers';

/**
 * The quantity badge appends the unit of measure only when present; the variant is
 * selected with a ternary inside t() so the i18next-parser keeps both literals.
 *
 * i18n keys
 * t('webedi:packing_list.products.quantity_badge', { available, total })
 * t('webedi:packing_list.products.quantity_badge_with_unit', { available, total, unit })
 */
export const ProductCardContent = ({
  entry,
  available,
  total,
  isOverlay = false,
  isSelected = false,
  leading,
  trailing,
}: {
  entry: LineItemEntry;
  available: number;
  total: number;
  isOverlay?: boolean;
  isSelected?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
}) => {
  const { t } = useTranslation();
  const { name, identifiers, unitOfMeasure } = getLineItemDisplay(entry.row, t);
  const isPlaced = available <= 0;
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 select-none',
        isPlaced ? 'border-gray-200 bg-gray-50 opacity-80' : 'border-gray-200 bg-white',
        !isPlaced && !isOverlay ? 'hover:border-gray-200 hover:shadow-xs' : '',
        isOverlay ? 'scale-105 cursor-grabbing shadow-lg' : '',
        isSelected ? 'ring-2 ring-procuros-green-500' : '',
      )}
    >
      <div className="flex shrink-0 flex-col items-center">
        {isPlaced ? (
          <CheckIcon className="size-4 text-green-600" aria-hidden="true" />
        ) : (
          <DragHandleIcon className="size-4 text-gray-300" />
        )}
      </div>
      {leading ? <div className="shrink-0">{leading}</div> : null}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm leading-5 font-normal text-gray-900">{name}</p>
        <LineItemIdentifiers parts={identifiers} />
      </div>
      <Badge className="shrink-0" color={isPlaced ? BadgeColor.green : BadgeColor.gray}>
        {isPlaced
          ? t('webedi:packing_list.products.placed')
          : unitOfMeasure
            ? t('webedi:packing_list.products.quantity_badge_with_unit', { available, total, unit: unitOfMeasure })
            : t('webedi:packing_list.products.quantity_badge', { available, total })}
      </Badge>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
};
